<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  tickets: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'taskUpdated'])

// Timeline state
const timeView = ref('weeks')
const currentDate = ref(new Date())
const timelineContent = ref(null)
const showTaskDetails = ref(null)

// Timeline data
const timelineData = computed(() => {
  if (!props.tickets || props.tickets.length === 0) {
    // Fallback to default data for demo purposes
    return defaultTimelineData
  }
  
  // Group tickets by category
  const categoryMap = {}
  
  for (const ticket of props.tickets) {
    const categoryType = ticket.type || 'Uncategorized'
    
    if (!categoryMap[categoryType]) {
      categoryMap[categoryType] = {
        type: categoryType,
        color: getCategoryColor(categoryType),
        tasks: []
      }
    }
    
    const start = parseTicketDate(ticket.created_at) || new Date()
    const end = parseTicketDate(ticket.updated_at) || new Date(start.getTime() + 3 * 24 * 60 * 60 * 1000)
    
    categoryMap[categoryType].tasks.push({
      id: ticket.id,
      name: ticket.title,
      assignee: {
        name: ticket.assignee_name || 'Unassigned',
        avatar: getInitials(ticket.assignee_name),
        role: ticket.assignee ? 'Assignee' : 'Unassigned'
      },
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      progress: ticket.importance ? ticket.importance * 10 : 50,
      status: ticket.status || 'pending'
    })
  }
  
  return Object.values(categoryMap)
})

// Status configurations
const statusColors = {
  completed: 'bg-green-500',
  in_progress: 'bg-blue-500',
  pending: 'bg-yellow-500',
  delayed: 'bg-red-500',
  open: 'bg-amber-500',
  testing: 'bg-purple-500',
  resolved: 'bg-teal-500',
  closed: 'bg-gray-500'
}

// Status class names for the history panel
const statusClassNames = {
  completed: 'bg-green-100 text-green-800',
  in_progress: 'bg-blue-100 text-blue-800',
  pending: 'bg-yellow-100 text-yellow-800',
  delayed: 'bg-red-100 text-red-800',
  open: 'bg-amber-100 text-amber-800',
  testing: 'bg-purple-100 text-purple-800',
  resolved: 'bg-teal-100 text-teal-800',
  closed: 'bg-gray-100 text-gray-800'
}

// Status bar colors for history panel progress bars
const statusBarColors = {
  completed: 'bg-green-500',
  in_progress: 'bg-blue-500',
  pending: 'bg-yellow-500',
  delayed: 'bg-red-500',
  open: 'bg-amber-500',
  testing: 'bg-purple-500',
  resolved: 'bg-teal-500',
  closed: 'bg-gray-500'
}

// Format status for display
const formatStatus = (status) => {
  return status.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ')
}

// Format date range for display
const formatDateRange = (start, end) => {
  const startDate = new Date(start)
  const endDate = new Date(end)
  
  return `${formatDate(startDate)} - ${formatDate(endDate)}`
}

// Format date for log display
const formatLogDate = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Format time for log display
const formatLogTime = (dateStr) => {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Add a function to format dates consistently
const formatDate = (date) => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date)
}

// Update timelineConfig computed
const timelineConfig = computed(() => {
  const today = new Date(currentDate.value)
  
  switch (timeView.value) {
    case 'days':
      return {
        columns: 14,
        width: 'minmax(60px, 1fr)',
        startDate: new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000)),
        endDate: new Date(today.getTime() + (6 * 24 * 60 * 60 * 1000)),
        getHeaderText: (date) => ({
          main: date.getDate(),
          sub: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()],
          dateRange: formatDate(date)
        })
      }
    case 'weeks':
      const weekStart = new Date(today)
      weekStart.setDate(today.getDate() - today.getDay() + 1) // Start from Monday
      return {
        columns: 28,
        width: 'minmax(40px, 1fr)',
        startDate: weekStart,
        endDate: new Date(weekStart.getTime() + (27 * 24 * 60 * 60 * 1000)),
        getHeaderText: (date) => ({
          main: `Week ${getWeekNumber(date)}`,
          sub: `${formatDate(date)} - ${formatDate(new Date(date.getTime() + (6 * 24 * 60 * 60 * 1000)))}`,
          dateRange: formatDate(date)
        })
      }
    case 'months':
      const yearStart = new Date(today.getFullYear(), 0, 1)
      return {
        columns: 12,
        width: 'minmax(120px, 1fr)',
        startDate: yearStart,
        endDate: new Date(today.getFullYear(), 11, 31),
        getHeaderText: (date) => ({
          main: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()],
          sub: date.getFullYear().toString(),
          dateRange: `${date.getFullYear()}`
        })
      }
    case 'years':
      return {
        columns: 10,
        width: 'minmax(150px, 1fr)',
        startDate: new Date(today.getFullYear() - 5, 0, 1),
        endDate: new Date(today.getFullYear() + 4, 11, 31),
        getHeaderText: (date) => ({
          main: date.getFullYear(),
          sub: 'Q1-Q4',
          dateRange: `${date.getFullYear()}`
        })
      }
  }
})

// Add helper function to get week number
const getWeekNumber = (date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 4 - (d.getDay() || 7))
  const yearStart = new Date(d.getFullYear(), 0, 1)
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
}

// Update timelineHeaders computed
const timelineHeaders = computed(() => {
  const config = timelineConfig.value
  const headers = []
  let current = new Date(config.startDate)

  while (headers.length < config.columns) {
    headers.push({
      ...config.getHeaderText(current),
      isWeekend: current.getDay() === 0 || current.getDay() === 6,
      date: new Date(current)
    })

    if (timeView.value === 'days' || timeView.value === 'weeks') {
      current.setDate(current.getDate() + 1)
    } else if (timeView.value === 'months') {
      current.setMonth(current.getMonth() + 1)
    } else {
      current.setFullYear(current.getFullYear() + 1)
    }
  }
  return headers
})

// Update getTaskPosition to use the new timeline config
const getTaskPosition = (startDate) => {
  const config = timelineConfig.value
  const start = new Date(startDate)
  const timelineStart = config.startDate
  const timelineEnd = config.endDate
  
  const totalTime = timelineEnd - timelineStart
  const timeSinceStart = start - timelineStart
  
  return Math.max(0, Math.min(100, (timeSinceStart / totalTime) * 100))
}

// Update getTaskWidth to use the new timeline config
const getTaskWidth = (startDate, endDate) => {
  const config = timelineConfig.value
  const start = new Date(startDate)
  const end = new Date(endDate)
  const timelineStart = config.startDate
  const timelineEnd = config.endDate
  
  const totalTime = timelineEnd - timelineStart
  const taskDuration = end - start
  
  return Math.max(0, Math.min(100, (taskDuration / totalTime) * 100))
}

const navigateTimeline = (direction) => {
  const amount = timeView.value === 'days' ? 7 : 
                 timeView.value === 'weeks' ? 4 : 
                 timeView.value === 'months' ? 1 : 1
                 
  const date = new Date(currentDate.value)
  if (direction === 'forward') {
    date.setDate(date.getDate() + amount)
  } else {
    date.setDate(date.getDate() - amount)
  }
  currentDate.value = date
}

const changeTimeView = (view) => {
  timeView.value = view
}

const handleTaskClick = (task) => {
  showTaskDetails.value = task
}

// Add computed property to flatten tasks
const flatTasks = computed(() => {
  return timelineData.value.flatMap(category => 
    category.tasks.map(task => ({
      ...task,
      categoryType: category.type,
      categoryColor: category.color
    }))
  )
})

// Add these new functions
const getDaysForWeek = (weekLabel) => {
  const weekNumber = parseInt(weekLabel.split(' ')[1])
  const startDay = {
    47: [16, 17, 18, 19, 20, 21, 22],
    48: [23, 24, 25, 26, 27, 28, 29],
    49: [30, 1, 2, 3, 4, 5, 6],
    50: [7, 8, 9, 10, 11, 12, 13]
  }
  return startDay[weekNumber] || []
}

const isCurrentDay = (day) => {
  const today = new Date()
  return today.getDate() === day && 
         today.getMonth() === 10 // November (0-based)
}

// Add drag state
const draggedTask = ref(null)
const dragStartX = ref(0)
const dragStartY = ref(0)
const originalPosition = ref(null)
const isDragging = ref(false)
const dragType = ref(null) // 'reorder' or 'reschedule'

// Add ghost element state
const ghostPosition = ref({ x: 0, y: 0, show: false })

// Update handleDragStart
const handleDragStart = (task, event, type = 'reorder') => {
  draggedTask.value = task
  dragType.value = type
  isDragging.value = true
  
  // Store initial positions
  dragStartX.value = event.clientX
  dragStartY.value = event.clientY
  originalPosition.value = {
    startDate: new Date(task.startDate),
    endDate: new Date(task.endDate),
    index: flatTasks.value.findIndex(t => t.id === task.id)
  }

  // Initialize ghost position
  ghostPosition.value = {
    x: getTaskPosition(task.startDate),
    y: originalPosition.value.index * 60, // height of each row
    show: true
  }

  document.body.classList.add('dragging')
}

// Update handleDragMove
const handleDragMove = (event) => {
  if (!isDragging.value || !draggedTask.value || !timelineContent.value) return

  const timelineRect = timelineContent.value.getBoundingClientRect()
  const mouseX = event.clientX - timelineRect.left
  const mouseY = event.clientY - timelineRect.top
  
  // Update ghost position
  ghostPosition.value = {
    x: (mouseX / timelineRect.width) * 100,
    y: Math.floor(mouseY / 60) * 60, // Snap to row height
    show: true
  }

  if (dragType.value === 'reschedule') {
    const percentageAcross = mouseX / timelineRect.width
    const totalTime = timelineConfig.value.endDate - timelineConfig.value.startDate
    const timeAtMouse = new Date(timelineConfig.value.startDate.getTime() + (totalTime * percentageAcross))
    const taskDuration = new Date(originalPosition.value.endDate) - new Date(originalPosition.value.startDate)
    
    const newStartDate = new Date(timeAtMouse)
    const newEndDate = new Date(timeAtMouse.getTime() + taskDuration)
    
    newStartDate.setHours(0, 0, 0, 0)
    newEndDate.setHours(23, 59, 59, 999)
    
    draggedTask.value.startDate = newStartDate.toISOString().split('T')[0]
    draggedTask.value.endDate = newEndDate.toISOString().split('T')[0]
  } else if (dragType.value === 'resize-start') {
    // Calculate new start date while keeping end date fixed
    const mouseX = event.clientX - timelineRect.left
    const percentageAcross = mouseX / timelineRect.width
    const totalTime = timelineConfig.value.endDate - timelineConfig.value.startDate
    const newStartDate = new Date(timelineConfig.value.startDate.getTime() + (totalTime * percentageAcross))
    newStartDate.setHours(0, 0, 0, 0)
    
    // Ensure start date isn't after end date
    if (newStartDate < new Date(draggedTask.value.endDate)) {
      draggedTask.value.startDate = newStartDate.toISOString().split('T')[0]
    }
  } else if (dragType.value === 'resize-end') {
    // Calculate new end date while keeping start date fixed
    const mouseX = event.clientX - timelineRect.left
    const percentageAcross = mouseX / timelineRect.width
    const totalTime = timelineConfig.value.endDate - timelineConfig.value.startDate
    const newEndDate = new Date(timelineConfig.value.startDate.getTime() + (totalTime * percentageAcross))
    newEndDate.setHours(23, 59, 59, 999)
    
    // Ensure end date isn't before start date
    if (newEndDate > new Date(draggedTask.value.startDate)) {
      draggedTask.value.endDate = newEndDate.toISOString().split('T')[0]
    }
  }
}

// Update handleDragEnd
const handleDragEnd = () => {
  if (!isDragging.value) return

  isDragging.value = false
  draggedTask.value = null
  dragType.value = null
  ghostPosition.value.show = false
  document.body.classList.remove('dragging')

  emit('taskUpdated', draggedTask.value)
}

// Add event listeners
onMounted(() => {
  window.addEventListener('mousemove', handleDragMove)
  window.addEventListener('mouseup', handleDragEnd)
})

// Add cleanup on component unmount
onUnmounted(() => {
  window.removeEventListener('mousemove', handleDragMove)
  window.removeEventListener('mouseup', handleDragEnd)
})

// Parse ticket date string to Date object
function parseTicketDate(dateStr) {
  // Handles both 'DD.MM.YYYY HH:mm:ss' and ISO
  if (!dateStr) return null
  if (dateStr.includes('-') && dateStr.includes('T')) return new Date(dateStr)
  const [d, m, yAndTime] = dateStr.split('.')
  if (!d || !m || !yAndTime) return null
  const [y, time] = [yAndTime.slice(0, 4), yAndTime.slice(5)]
  return new Date(`${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T${time || '00:00:00'}`)
}

// Get category color based on type
function getCategoryColor(type) {
  const colorMap = {
    'bug': '#D35400',
    'feature': '#4A90E2',
    'improvement': '#2E8B57',
    'support': '#B8860B',
    'Uncategorized': '#8B4513'
  }
  return colorMap[type] || '#8B4513'
}

// Get initials from name
function getInitials(name) {
  if (!name) return 'UN'
  return name.split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
}

// Default timeline data (used only when no tickets provided)
const defaultTimelineData = [
  {
    type: 'Site Preparation',
    color: '#8B4513',
    tasks: [
      {
        id: 1,
        name: 'Site clearing',
        assignee: {
          name: 'Mary Smith',
          avatar: 'MS',
          role: 'Project Manager'
        },
        startDate: '2023-11-20',
        endDate: '2023-11-25',
        progress: 100,
        status: 'completed'
      },
      {
        id: 2,
        name: 'Leveling',
        assignee: {
          name: 'John Construction',
          avatar: 'JC',
          role: 'Site Engineer'
        },
        startDate: '2023-11-23',
        endDate: '2023-12-05',
        progress: 75,
        status: 'in_progress'
      }
    ]
  },
  {
    type: 'Foundation Work',
    color: '#B8860B',
    tasks: [
      {
        id: 3,
        name: 'Foundation Planning',
        assignee: {
          name: 'Sarah Design',
          avatar: 'SD',
          role: 'Architect'
        },
        startDate: '2023-11-28',
        endDate: '2023-12-02',
        progress: 30,
        status: 'in_progress'
      },
      {
        id: 4,
        name: 'Concrete Work',
        assignee: {
          name: 'John Construction',
          avatar: 'JC',
          role: 'Site Engineer'
        },
        startDate: '2023-12-01',
        endDate: '2023-12-07',
        progress: 20,
        status: 'in_progress'
      }
    ]
  },
  {
    type: 'Design & Review',
    color: '#4A90E2',
    tasks: [
      {
        id: 5,
        name: 'Design Review',
        assignee: {
          name: 'Sarah Design',
          avatar: 'SD',
          role: 'Architect'
        },
        startDate: '2023-11-25',
        endDate: '2023-11-30',
        progress: 90,
        status: 'in_progress'
      }
    ]
  }
]

// Sort tasks by date for historical log
const sortedTasksByDate = computed(() => {
  return [...flatTasks.value].sort((a, b) => {
    return new Date(a.startDate) - new Date(b.startDate)
  })
})

// Function to determine if we should show a date divider
const shouldShowDateDivider = (task, index) => {
  if (index === 0) return true
  
  const currentDate = new Date(task.startDate)
  const prevDate = new Date(sortedTasksByDate.value[index - 1].startDate)
  
  return currentDate.toDateString() !== prevDate.toDateString()
}

// Add selected task state
const selectedTask = ref(null)
const newComment = ref('')

// Mock task updates storage (in a real app, this would come from a database)
const taskUpdates = ref([
  {
    taskId: 1,
    updates: [
      {
        id: 1,
        date: '2023-11-20T09:00:00',
        from: {
          name: 'Mary Smith',
          avatar: 'MS',
          role: 'Project Manager'
        },
        message: 'Started clearing the west section of the site.',
        statusChange: 'in_progress',
        progressChange: 20
      },
      {
        id: 2,
        date: '2023-11-22T14:30:00',
        from: {
          name: 'Mary Smith',
          avatar: 'MS',
          role: 'Project Manager'
        },
        message: 'Completed the initial clearing phase ahead of schedule.',
        progressChange: 60
      },
      {
        id: 3,
        date: '2023-11-25T11:15:00',
        from: {
          name: 'John Construction',
          avatar: 'JC',
          role: 'Site Engineer'
        },
        message: 'Verified all site clearing work. Moving to leveling phase next.',
        statusChange: 'completed',
        progressChange: 100
      }
    ]
  },
  {
    taskId: 2,
    updates: [
      {
        id: 1,
        date: '2023-11-23T08:45:00',
        from: {
          name: 'John Construction',
          avatar: 'JC',
          role: 'Site Engineer'
        },
        message: 'Started the leveling process on the east section.',
        statusChange: 'in_progress',
        progressChange: 10
      },
      {
        id: 2,
        date: '2023-11-29T16:20:00',
        from: {
          name: 'John Construction',
          avatar: 'JC',
          role: 'Site Engineer'
        },
        message: 'Leveling is progressing well. Approximately 75% complete.',
        progressChange: 75
      }
    ]
  }
])

// Add getTaskUpdates function
const getTaskUpdates = (task) => {
  if (!task) return []
  
  // Find updates for this task
  const taskHistory = taskUpdates.value.find(t => t.taskId === task.id)
  
  if (!taskHistory) {
    // If no updates found, create basic history from task data
    const baseUpdates = [
      {
        id: 1,
        date: task.startDate + 'T09:00:00',
        from: task.assignee,
        message: `Task "${task.name}" created and assigned.`,
        statusChange: task.status,
        progressChange: task.progress > 0 ? task.progress : undefined
      }
    ]
    
    // Add the new task history
    taskUpdates.value.push({
      taskId: task.id,
      updates: baseUpdates
    })
    
    return baseUpdates
  }
  
  return taskHistory.updates.sort((a, b) => new Date(a.date) - new Date(b.date))
}

// Add addTaskComment function
const addTaskComment = () => {
  if (!newComment.trim() || !selectedTask.value) return
  
  // Find or create task history
  let taskHistory = taskUpdates.value.find(t => t.taskId === selectedTask.value.id)
  
  if (!taskHistory) {
    taskHistory = {
      taskId: selectedTask.value.id,
      updates: []
    }
    taskUpdates.value.push(taskHistory)
  }
  
  // Create new update
  const newUpdate = {
    id: Date.now(),
    date: new Date().toISOString(),
    from: {
      name: 'Current User',
      avatar: 'CU',
      role: 'User'
    },
    message: newComment.value
  }
  
  // Add to updates
  taskHistory.updates.push(newUpdate)
  
  // Clear input
  newComment.value = ''
  
  // Emit event for update
  emit('taskUpdated', {
    task: selectedTask.value,
    update: newUpdate
  })
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-8">
    <div class="bg-white rounded-xl shadow-2xl w-[95vw] h-[90vh] flex flex-col overflow-hidden">
      <!-- Timeline Header -->
      <div class="flex items-center justify-between p-4 border-b bg-white">
        <div class="flex items-center gap-6">
          <h2 class="text-xl font-semibold text-gray-800">Timeline View</h2>
          
          <!-- Updated Time Scale Buttons -->
          <div class="flex bg-white rounded-full shadow-sm border">
            <button 
              v-for="view in ['Days', 'Weeks', 'Months', 'Years']"
              :key="view.toLowerCase()"
              @click="changeTimeView(view.toLowerCase())"
              class="px-6 py-2 text-sm transition-all first:rounded-l-full last:rounded-r-full relative"
              :class="[
                timeView === view.toLowerCase() 
                  ? 'text-pink-600 bg-white z-10' 
                  : 'text-gray-600 hover:bg-gray-50'
              ]"
            >
              {{ view }}
              <!-- Active Indicator -->
              <div v-if="timeView === view.toLowerCase()" 
                   class="absolute inset-0 border-2 border-pink-200 rounded-full">
              </div>
            </button>
          </div>

          <!-- Navigation Controls -->
          <div class="flex items-center gap-1">
            <button @click="navigateTimeline('back')" 
                    class="p-2 text-gray-400 hover:text-gray-600 rounded">
              <i class="fas fa-chevron-left"></i>
            </button>
            <button @click="currentDate = new Date()" 
                    class="px-4 py-1 text-sm text-pink-600 bg-pink-50 rounded-full hover:bg-pink-100">
              Today
            </button>
            <button @click="navigateTimeline('forward')" 
                    class="p-2 text-gray-400 hover:text-gray-600 rounded">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- Close Button -->
        <button @click="$emit('close')" 
                class="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Main Content Area -->
      <div class="flex-1 overflow-hidden flex">
        <!-- Left Sidebar - Track Headers -->
        <div class="w-60 flex-shrink-0 bg-gray-50 border-r border-gray-200">
          <!-- Header Spacer - Match timeline header height -->
          <div class="h-[88px] border-b border-gray-200 bg-white p-4 flex items-end">
            <span class="text-sm font-medium text-gray-500">Tasks</span>
          </div>

          <!-- Task List -->
          <div class="overflow-y-auto h-full pb-10">
            <div v-for="task in flatTasks" 
                 :key="task.id"
                 class="h-[60px] border-b border-gray-200 p-3 flex items-center hover:bg-gray-100">
              <div class="flex items-center gap-3 w-full">
                <!-- Task Icon -->
                <div class="w-8 h-8 rounded flex items-center justify-center"
                     :style="{ backgroundColor: task.categoryColor + '20', color: task.categoryColor }">
                  <i class="fas fa-tasks"></i>
                </div>
                <!-- Task Info -->
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium truncate">{{ task.name }}</div>
                  <div class="text-xs text-gray-500">{{ task.categoryType }}</div>
                </div>
                <!-- Assignee -->
                <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs"
                     :title="task.assignee.name">
                  {{ task.assignee.avatar }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Timeline Grid Area -->
        <div class="flex-1 overflow-hidden flex flex-col">
          <!-- Timeline Header -->
          <div class="h-[88px] sticky top-0 bg-white border-b border-gray-200 z-10">
            <div class="grid h-full" 
                 :style="{ gridTemplateColumns: `repeat(${timelineConfig.columns}, ${timelineConfig.width})` }">
              <template v-for="header in timelineHeaders" :key="header.date">
                <div class="border-r border-gray-200 p-2"
                     :class="{ 'bg-gray-50/30': header.isWeekend }">
                  <div class="text-sm font-medium text-gray-600">{{ header.main }}</div>
                  <div class="text-xs text-gray-500">{{ header.sub }}</div>
                </div>
              </template>
            </div>
          </div>

          <!-- Timeline Content -->
          <div class="flex-1 overflow-x-auto timeline-content" ref="timelineContent">
            <div class="relative min-w-max">
              <!-- Background Grid -->
              <div class="absolute inset-0 grid" 
                   :style="{ gridTemplateColumns: `repeat(${timelineConfig.columns}, ${timelineConfig.width})` }">
                <template v-for="header in timelineHeaders" :key="header.date">
                  <div class="border-r border-gray-100 h-full"
                       :class="{ 'bg-gray-50/30': header.isWeekend }">
                  </div>
                </template>
              </div>

              <!-- Task Rows -->
              <div>
                <div v-for="task in flatTasks" 
                     :key="task.id"
                     class="h-[60px] relative border-b border-gray-200 group hover:bg-gray-50/50">
                  <!-- Task Progress Bar -->
                  <div class="absolute h-10 rounded-lg mt-[10px] mx-2 cursor-move"
                       :class="{ 
                         'dragging': isDragging && draggedTask?.id === task.id,
                         'opacity-50': isDragging && draggedTask?.id === task.id,
                         'z-10': isDragging && draggedTask?.id === task.id 
                       }"
                       :style="{
                         left: `${getTaskPosition(task.startDate)}%`,
                         width: `${getTaskWidth(task.startDate, task.endDate)}%`,
                         backgroundColor: task.categoryColor
                       }"
                       @mousedown.left="handleDragStart(task, $event, 'reschedule')"
                       @click.stop="handleTaskClick(task)"
                       @mouseenter="showTaskDetails = task"
                       @mouseleave="showTaskDetails = null">
                    <!-- Left resize handle -->
                    <div class="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/20"
                         @mousedown.left.stop="handleDragStart(task, $event, 'resize-start')">
                    </div>
                    
                    <!-- Progress Overlay -->
                    <div class="h-full rounded-lg bg-white/10 backdrop-blur-sm 
                                flex items-center justify-between px-3 group-hover:shadow-lg transition-all">
                      <span class="text-white text-sm font-medium">{{ task.name }}</span>
                      <div class="flex items-center gap-2">
                        <span class="text-xs text-white/90 px-2 py-0.5 rounded-full bg-black/20">
                          {{ task.progress }}%
                        </span>
                        <div :class="['w-2 h-2 rounded-full', statusColors[task.status]]"></div>
                      </div>
                    </div>

                    <!-- Right resize handle -->
                    <div class="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/20"
                         @mousedown.left.stop="handleDragStart(task, $event, 'resize-end')">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Task History Panel (Chat-like) -->
        <div class="w-96 flex-shrink-0 border-l border-gray-200 flex flex-col bg-gray-50">
          <div class="h-[88px] bg-white border-b border-gray-200 p-4 flex items-center justify-between">
            <span class="text-sm font-medium text-gray-500">Task History</span>
            <div class="flex items-center gap-2">
              <button class="p-2 text-gray-400 hover:text-gray-600 rounded">
                <i class="fas fa-filter"></i>
              </button>
              <button class="p-2 text-gray-400 hover:text-gray-600 rounded">
                <i class="fas fa-sort"></i>
              </button>
            </div>
          </div>
          
          <!-- Historical Log -->
          <div class="flex-1 overflow-y-auto p-4">
            <div class="space-y-4">
              <!-- Tasks as log entries -->
              <template v-for="(task, index) in sortedTasksByDate" :key="task.id">
                <!-- Date divider -->
                <div v-if="shouldShowDateDivider(task, index)" class="flex items-center my-6">
                  <div class="flex-grow border-t border-gray-200"></div>
                  <div class="mx-4 flex-shrink-0 text-xs text-gray-500">
                    {{ formatLogDate(task.startDate) }}
                  </div>
                  <div class="flex-grow border-t border-gray-200"></div>
                </div>
                
                <!-- Task log entry -->
                <div class="flex items-start gap-3">
                  <!-- Avatar -->
                  <div class="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs"
                       :title="task.assignee.name">
                    {{ task.assignee.avatar }}
                  </div>
                  
                  <!-- Message bubble -->
                  <div class="flex-1 relative">
                    <div class="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                      <div class="flex justify-between items-start mb-1">
                        <span class="font-medium text-sm">{{ task.assignee.name }}</span>
                        <span class="text-xs text-gray-500">{{ formatLogTime(task.startDate) }}</span>
                      </div>
                      
                      <div class="text-sm text-gray-700">
                        <span class="font-medium">{{ task.name }}</span> - 
                        <span :class="['text-xs px-2 py-0.5 rounded-full', statusClassNames[task.status]]">
                          {{ formatStatus(task.status) }}
                        </span>
                      </div>
                      
                      <div class="mt-2 flex items-center text-xs">
                        <span class="text-gray-500">Progress: </span>
                        <div class="ml-2 flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div :class="['h-full', statusBarColors[task.status]]" 
                               :style="{ width: `${task.progress}%` }"></div>
                        </div>
                        <span class="ml-1 text-gray-600">{{ task.progress }}%</span>
                      </div>
                      
                      <div class="mt-2 text-xs text-gray-500 flex items-center">
                        <i class="fas fa-calendar-alt mr-1"></i>
                        {{ formatDateRange(task.startDate, task.endDate) }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Task Details Popup -->
  <div v-if="showTaskDetails" 
       class="absolute bg-white rounded-lg shadow-xl p-4 max-w-md"
       :style="{
         left: `${getTaskPosition(showTaskDetails.startDate)}%`,
         top: '50%'
       }">
    <div class="flex items-start gap-3">
      <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
        {{ showTaskDetails.assignee.avatar }}
      </div>
      <div class="flex-1">
        <h3 class="font-medium">{{ showTaskDetails.name }}</h3>
        <div class="text-sm text-gray-500">{{ showTaskDetails.assignee.name }}</div>
        <div class="text-xs text-gray-400">{{ showTaskDetails.assignee.role }}</div>
        <div class="mt-2 flex items-center gap-2">
          <div class="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div class="h-full bg-green-500" :style="{ width: `${showTaskDetails.progress}%` }"></div>
          </div>
          <span class="text-xs text-gray-500">{{ showTaskDetails.progress }}%</span>
        </div>
        <div class="mt-2 text-xs text-gray-500">
          {{ new Date(showTaskDetails.startDate).toLocaleDateString() }} - 
          {{ new Date(showTaskDetails.endDate).toLocaleDateString() }}
        </div>
        <button @click="selectedTask = showTaskDetails; showTaskDetails = null" 
                class="mt-3 px-3 py-1 bg-pink-500 text-white text-xs rounded-lg hover:bg-pink-600">
          View Task Chat
        </button>
      </div>
    </div>
  </div>

  <!-- Selected Task Chat Panel -->
  <div v-if="selectedTask" class="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-8">
    <div class="bg-white rounded-xl shadow-2xl w-[600px] h-[80vh] flex flex-col overflow-hidden">
      <!-- Task Chat Header -->
      <div class="flex items-center justify-between p-4 border-b bg-white">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full" :style="{ backgroundColor: selectedTask.categoryColor + '20' }">
            <div class="w-full h-full rounded-full flex items-center justify-center">
              <span class="text-sm font-medium" :style="{ color: selectedTask.categoryColor }">
                {{ selectedTask.assignee.avatar }}
              </span>
            </div>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-800">{{ selectedTask.name }}</h2>
            <div class="text-sm text-gray-500">{{ selectedTask.categoryType }}</div>
          </div>
        </div>
        
        <button @click="selectedTask = null" class="text-gray-500 hover:text-gray-700 p-2">
          <span class="text-xl">&times;</span>
        </button>
      </div>
      
      <!-- Task Info Bar -->
      <div class="bg-gray-50 p-4 border-b">
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-4">
            <div>
              <span class="text-xs text-gray-500">Assignee</span>
              <div class="flex items-center gap-2 mt-1">
                <div class="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs">
                  {{ selectedTask.assignee.avatar }}
                </div>
                <span class="text-sm font-medium">{{ selectedTask.assignee.name }}</span>
              </div>
            </div>
            <div>
              <span class="text-xs text-gray-500">Status</span>
              <div class="mt-1">
                <span :class="['text-xs px-2 py-1 rounded-full', statusClassNames[selectedTask.status]]">
                  {{ formatStatus(selectedTask.status) }}
                </span>
              </div>
            </div>
          </div>
          <div>
            <span class="text-xs text-gray-500">Progress</span>
            <div class="mt-1 flex items-center gap-2">
              <div class="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div :class="['h-full', statusBarColors[selectedTask.status]]" 
                     :style="{ width: `${selectedTask.progress}%` }"></div>
              </div>
              <span class="text-xs font-medium">{{ selectedTask.progress }}%</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Task Chat History -->
      <div class="flex-1 overflow-y-auto p-4 bg-gray-50">
        <div class="space-y-4">
          <!-- Initial task creation message -->
          <div class="flex justify-center">
            <div class="bg-gray-100 text-gray-500 text-xs px-3 py-1 rounded-full">
              Task created on {{ formatLogDate(selectedTask.startDate) }}
            </div>
          </div>
          
          <!-- System update for task start -->
          <div class="flex justify-center">
            <div class="flex items-center gap-2 bg-blue-50 text-blue-600 text-xs px-3 py-1 rounded-full">
              <i class="fas fa-play-circle"></i>
              <span>Task started</span>
            </div>
          </div>
          
          <!-- Task updates -->
          <div v-for="(update, index) in getTaskUpdates(selectedTask)" :key="index" class="flex items-start gap-3">
            <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs">
              {{ update.from.avatar }}
            </div>
            <div class="flex-1">
              <div class="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                <div class="flex justify-between items-start mb-1">
                  <span class="font-medium text-sm">{{ update.from.name }}</span>
                  <span class="text-xs text-gray-500">{{ formatLogTime(update.date) }}</span>
                </div>
                <div class="text-sm text-gray-700">
                  {{ update.message }}
                </div>
                <div v-if="update.statusChange" class="mt-2 flex items-center gap-2">
                  <span class="text-xs text-gray-500">Status changed to:</span>
                  <span :class="['text-xs px-2 py-0.5 rounded-full', statusClassNames[update.statusChange]]">
                    {{ formatStatus(update.statusChange) }}
                  </span>
                </div>
                <div v-if="update.progressChange !== undefined" class="mt-2 flex items-center text-xs">
                  <span class="text-gray-500">Progress updated: </span>
                  <div class="ml-2 flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div :class="['h-full', statusBarColors[selectedTask.status]]" 
                         :style="{ width: `${update.progressChange}%` }"></div>
                  </div>
                  <span class="ml-1 text-gray-600">{{ update.progressChange }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Chat Input -->
      <div class="p-4 border-t bg-white">
        <div class="flex items-center gap-2">
          <input 
            v-model="newComment"
            type="text"
            placeholder="Type a comment..."
            class="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            @keyup.enter="addTaskComment"
          />
          <button 
            @click="addTaskComment"
            class="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 flex items-center gap-2"
            :disabled="!newComment.trim()"
          >
            <span>Send</span>
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add ghost element -->
  <div v-if="ghostPosition.show && isDragging" 
       class="absolute pointer-events-none"
       :style="{
         left: `${ghostPosition.x}%`,
         top: `${ghostPosition.y}px`,
         width: `${getTaskWidth(draggedTask.startDate, draggedTask.endDate)}%`,
         height: '40px',
         transform: 'translateY(10px)',
         backgroundColor: 'rgba(59, 130, 246, 0.5)',
         border: '2px dashed #3B82F6',
         borderRadius: '0.5rem',
         zIndex: 50
       }">
  </div>
</template>

<style scoped>
/* Custom scrollbar styles */
.overflow-x-auto {
  scrollbar-width: thin;
  scrollbar-color: #E5E7EB transparent;
}

.overflow-x-auto::-webkit-scrollbar {
  height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background-color: #E5E7EB;
  border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background-color: #D1D5DB;
}

/* Prevent text selection */
.user-select-none {
  user-select: none;
}

/* Fix height classes */
.h-\[64px\] {
  height: 64px;
}

.h-10 {
  height: 2.5rem;
}

.mt-\[12px\] {
  margin-top: 12px;
}

/* Add these new styles */
.ml-60 {
  margin-left: 15rem;
}

/* Ensure proper stacking context */
.z-20 {
  z-index: 20;
}

/* Smooth transitions */
.transition-all {
  transition: all 0.2s ease-in-out;
}

/* Hover effects */
.group:hover .group-hover\:shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Add grid styles */
.grid-cols-7 {
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

/* Add these new styles */
.dragging {
  pointer-events: none;
  cursor: grabbing !important;
}

.cursor-ew-resize {
  cursor: ew-resize;
}

.cursor-move {
  cursor: move;
}

/* Prevent text selection while dragging */
.dragging * {
  user-select: none;
}

.cursor-grab {
  cursor: grab;
}
</style> 